import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { Navigate } from 'react-router-dom';

// Data Layer
import { useNetworkObserver } from 'data-layer-cr/dist/network';

import {
  Dialog,
  IconButton,
  Theme,
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { ArrowLeft, XCircle } from 'react-feather';

import {
  BasePageQuotation,
  Footer,
  HeaderWithPercentBar,
  backgroundThemes,
  themeMethods,
} from 'design-system';

// Helper Functions
import getProgressPercent from 'src/functions/getProgressPercent';
import scrollToTopDialog from 'src/functions/scrollToTopDialog';

// Redux Actions
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';
import { getPricingProfiles } from 'src/store/pricingProfiles/actions';
import { REQUEST_PRICING_PROFILES } from 'src/store/pricingProfiles/requestIdentifiers';
import { requestQuotationUpdate } from 'src/store/quotation/actions';
import { REQUEST_PRICING_INITIAL } from 'src/store/quotation/requestIdentifiers';
import { setStepper } from 'src/store/steppers/actions';
// Extra
import { RootState } from 'src/store/rootReducer';

// Icons
import CarInfoIcon from '../../../Icons/CarInfoIcon';
import ProfileInfoIcon from '../../../Icons/ProfileInfoIcon';

// Local Models
import CarItem from '../../../../models/helperTypes/CarItem';
import CustomerItem from '../../../../models/helperTypes/CustomerItem';

// Forms
import CarDetailsForm from '../CarDetailsForm';
import ClientForm from '../ClientForm';
import LicenseForm from '../LicenseForm';

const useStyles = makeStyles((theme: Theme) => {
  const blue = 'linear-gradient(180deg, #CEE8FF 10.19%, #FFFFFF 100%)';
  const paperBackgroundColor =
    'linear-gradient(359.66deg, rgb(242, 244, 243) 13.26%, #FFFFFF 88.97%)';
  const grey =
    'linear-gradient(359.66deg, rgba(242, 244, 243,0.4) 13.26%, rgba(255, 255, 255,0.4) 88.97%)';
  return createStyles({
    root: {
      display: 'flex',
      flexFlow: 'column',
      flexGrow: 1,
    },
    container: {
      flex: 1,
      backgroundSize: '100%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '0% 20%',
      backgroundImage: (isFirstPage) =>
        isFirstPage
          ? `${themeMethods.getCurrentMonthTheme(
              new Date(),
              backgroundThemes.LOGIN_PAGE,
            )}, ${blue}`
          : `${grey}, ${themeMethods.getCurrentMonthTheme(
              new Date(),
              backgroundThemes.QUOTATION_PAGE,
            )}`,
      [theme.breakpoints.down('lg')]: {
        backgroundPosition: (isFirstPage) =>
          isFirstPage ? 'bottom' : '0%, 20%',
      },
      [theme.breakpoints.up('xl')]: {
        backgroundPosition: 'bottom',
      },
      [theme.breakpoints.down('sm')]: {
        backgroundImage: (isFirstPage) => (isFirstPage ? blue : grey),
      },
    },
    elevated: {
      zIndex: 2,
    },
    modalContentWrapper: {
      display: 'flex',
      justifyContent: 'center',
    },
    paperBackground: {
      background: paperBackgroundColor,
      overflowX: 'hidden',
    },
    modalContent: {
      padding: theme.spacing(1),
      maxWidth: 723,
      display: 'inline-block',
      marginLeft: 'auto',
      marginRight: 'auto',
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
      },
    },
    stepperContainer: {
      zIndex: theme.zIndex.appBar,
      position: 'sticky',
      backgroundColor: '#FDFDFD',
      margin: -8,
      marginBottom: 0,
      paddingTop: theme.spacing(2) + 1,
      paddingBottom: theme.spacing(3),
      [theme.breakpoints.up('sm')]: {
        left: theme.spacing(2),
        right: theme.spacing(2),
      },
      top: theme.mixins.toolbar.minHeight,
      [theme.breakpoints.up('sm')]: {
        top: 64,
      },
    },
    stepperItem: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    IconButtonGoback: {
      width: 'max-content',
    },
  });
});

export const stepperTitles = ['Datos del contratante', 'Datos del auto'];

export const stepperIcons = [<ProfileInfoIcon />, <CarInfoIcon />];

export const QuotationFormManager = ({
  driverData,
  carData,
  isOpen,
  currentPageStore,
  pricingProfiles,
  reqQuotationUpdate,
  onBackdropClick,
  setStepperAction,
  getPricingProfilesAction,
}: Props): JSX.Element => {
  const [formCompleted, setIsFormCompleted] = useState(false);
  const [showLicenseError, setShowLicenseError] = useState(false);

  const requestingQuotationState = useNetworkObserver(REQUEST_PRICING_INITIAL);
  const pricingProfilesRequestStatus = useNetworkObserver(
    REQUEST_PRICING_PROFILES,
  );
  const rootElement = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  /**
   * The first page has additional render logic
   */
  const isFirstPage = useMemo(() => currentPageStore === 0, [currentPageStore]);
  const classes = useStyles(isFirstPage);

  /**
   *  Make sure to only fetch pricing profiles when the store is empty.
   *  The reason we fetch it in here instead of license form, is because
   *  useFormHook validation is static, so the first render will always
   *  result in an empty array since it doesn't wait for the endpoint to be called,
   *  therefore, validation will be "not required", since it doesn't have any profiles.
   */
  useEffect(() => {
    if (
      pricingProfiles.length <= 0 &&
      pricingProfilesRequestStatus === 'initial'
    ) {
      getPricingProfilesAction();
    }
  }, [pricingProfiles, getPricingProfilesAction, pricingProfilesRequestStatus]);

  /**
   * Will call requestQuotationUpdate when formCompleted is set to "true"
   */
  useEffect(() => {
    if (formCompleted) {
      reqQuotationUpdate(carData, driverData);
    }
  }, [formCompleted, carData, driverData, reqQuotationUpdate]);

  /**
   * Ensures that the scroll is at 0 in every page render (for automation purposes)
   */
  useEffect(() => scrollToTopDialog(rootElement), [currentPageStore]);

  /**
   * Navigates to the next page registered in the form manager.
   */
  function goToNextPage(): void {
    setStepperAction(currentPageStore + 1);
  }

  /**
   * Navigates to the previous page registered in the form manager.
   */
  function goToBackPage(): void {
    setStepperAction(currentPageStore - 1);
  }

  // Don't need to fetch licenses since FilterDrawer is already doing it
  const licenseForm = (
    <LicenseForm
      data-testid="license-form"
      onSubmit={goToNextPage}
      pricingProfiles={pricingProfiles}
    />
  );

  const clientForm = (
    <ClientForm data-testid="client-form" onSubmit={goToNextPage} />
  );

  const carDetails = (
    <CarDetailsForm
      data-testid="car-details-form"
      loading={requestingQuotationState === 'requesting'}
      onSubmit={(): void => {
        setIsFormCompleted(true);
      }}
      onLicenseError={(value: boolean) => setShowLicenseError(value)}
    />
  );

  const pages = [licenseForm, clientForm, carDetails];

  const backgroundColor = isFirstPage ? '#CEE8FF' : 'white';

  return (
    <Dialog
      data-testid="quotation-form-id"
      open={isOpen}
      fullScreen
      classes={{
        paper: classes.paperBackground,
      }}
    >
      <div className={classes.root} ref={rootElement}>
        <div className={clsx(classes.container, classes.elevated)}>
          <BasePageQuotation
            data-testid="base-page"
            /** This is just defined to help center the title vs the right actions */
            leftActions={
              currentPageStore > 0 &&
              !showLicenseError && (
                <IconButton
                  data-testid="close-button"
                  onClick={goToBackPage}
                  className={classes.IconButtonGoback}
                >
                  <ArrowLeft />
                </IconButton>
              )
            }
            backgroundColor={backgroundColor}
            stepperItem={
              !isFirstPage &&
              !showLicenseError && (
                <HeaderWithPercentBar
                  data-testid="stepper"
                  goToBackPage={goToBackPage}
                  icon={stepperIcons[currentPageStore - 1]}
                  stepperTitle={stepperTitles[currentPageStore - 1]}
                  currentProgress={getProgressPercent(
                    currentPageStore,
                    stepperTitles.length,
                  )}
                />
              )
            }
            rightActions={
              !showLicenseError && (
                <IconButton
                  data-testid="close-button"
                  onClick={(): void => {
                    setStepperAction(0);
                    onBackdropClick();
                  }}
                >
                  <XCircle />
                </IconButton>
              )
            }
          >
            {/** Rendering current page step */}
            <div className={classes.modalContentWrapper}>
              <div className={classes.modalContent}>
                {requestingQuotationState === 'success' && (
                  <Navigate to="/dynamicQuotation" />
                )}
                {pages[currentPageStore]}
              </div>
            </div>
          </BasePageQuotation>
        </div>
      </div>
      {/** Footer Section */}
      <div className={classes.elevated}>
        <Footer
          IsMobile={isMobile}
          country="Costa Rica"
          linkText="Consulte nuestros términos y condiciones"
          linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
        />
      </div>
    </Dialog>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  driverData: CustomerItem;
  carData: CarItem;
  currentPageStore: number;
  pricingProfiles: PricingProfileResponse[];
} => ({
  driverData: state.customer.currentCustomer,
  carData: state.car.currentCar,
  currentPageStore: state.stepper.currentPage,
  pricingProfiles: state.pricingProfiles.profiles,
});

const reduxActions = {
  reqQuotationUpdate: requestQuotationUpdate,
  setStepperAction: setStepper,
  getPricingProfilesAction: getPricingProfiles,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  isOpen: boolean;
  onBackdropClick: () => void;
}

export default connector(QuotationFormManager);
